import { addMinutes, getMinutes } from 'date-fns';
import jwtDecode from 'jwt-decode';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { cache } from 'swr';

import AsyncStorage from '@react-native-async-storage/async-storage';
import api from '@services/api';

interface AuthState {
  token: string | null;
}

interface SignInCredentials {
  code: string;
}

interface AuthContextData {
  token: string | null;
  loading: boolean;
  setToken(code: SignInCredentials): Promise<void>;
  signOut(): Promise<void>;
}

interface CallbackResponse {
  token: string;
}

interface TokenInterface {
  exp: number;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>({} as AuthState);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(async () => {
    await AsyncStorage.removeItem('@cartaoluiza:token');

    setData({} as AuthState);
  }, []);

  const setToken = useCallback(async ({ code }) => {
    if (!code) {
      throw new Error('Param code is required');
    }

    cache.clear();
    await AsyncStorage.removeItem('@cartaoluiza:token');

    const response = await api.get<CallbackResponse>(`auth/callback`, {
      params: { code }
    });

    const { token } = response.data;

    await AsyncStorage.setItem('@cartaoluiza:token', token);

    setData({ token });
  }, []);

  useEffect(() => {
    async function loadStoragedData(): Promise<void> {
      const token = await AsyncStorage.getItem('@cartaoluiza:token');
      let expired = true;
      let tokenData = null;

      try {
        tokenData = jwtDecode<TokenInterface>(token as string);
        const minutes = getMinutes(tokenData.exp);
        const newExp = addMinutes(tokenData.exp, minutes);

        expired = newExp.getTime() < Date.now() / 1000;
      } catch (error) {}

      if (!expired) {
        setData({ token });
      } else {
        await signOut();
      }

      setLoading(false);
    }

    if (process.env.USE_API_MOCK) {
      setData({ token: 'MOCK_TOKEN' });
      setLoading(false);
    } else {
      loadStoragedData();
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token: data.token,
        setToken,
        loading,
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('use Auth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
